// UserProfile.jsx - New component for users to view their own transactions
import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Divider
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StarsIcon from '@mui/icons-material/Stars';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HistoryIcon from '@mui/icons-material/History';
import api from '../services/api';

const WalletPage = ({keywordsData, language}) => {
  const [transactions, setTransactions] = useState([]);
  const [walletInfo, setWalletInfo] = useState(null);

  useEffect(() => {
    fetchUserTransactions();
    fetchUserWallet();
  }, []);

  const fetchUserTransactions = async () => {
    try {
      const response = await api.get('/payment/my-transactions');
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
    }
  };

  const fetchUserWallet = async () => {
    try {
      const response = await api.get('/payment/my-wallet');
      setWalletInfo(response.data);
    } catch (error) {
      console.error('Error fetching wallet info:', error);
    }
  };

  const cashOnDelvryTxt = language === 'ar' ? 'كاش عند التوصيل' : 'Cash on delivery';
  const walletTxt = language === 'ar' ? 'المحفظة' : 'Wallet';

  return (
    <Container maxWidth="lg" sx={{ 
      py: 4, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 48, color: '#FFD700' }} />
        <Typography variant="h4" sx={{ 
          color: '#FFD700',
          fontWeight: 900,
          letterSpacing: '2px'
        }}>
          {language === 'ar' ? 'محفظتي' : 'Wallet'}
        </Typography>
      </Box>

      {/* Wallet Summary Card */}
      {walletInfo && (
        <Card sx={{ 
          mb: 4,
          backgroundColor: '#0D0D0D',
          border: '1px solid #2D2D2D',
          borderRadius: '20px',
          boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            border: '1px solid #FFD700',
            boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
          }
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
              gap: 3 
            }}>
              {/* Wallet ID */}
              <Box sx={{ 
                p: 2,
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                border: '1px solid #2D2D2D',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 20, color: '#FFD700' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'رقم معرف المحفظة' : 'Wallet ID'}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  direction: 'ltr',
                  textAlign: 'left'
                }}>
                  {walletInfo.wallet_id}
                </Typography>
              </Box>

              {/* Wallet Balance */}
              <Box sx={{ 
                p: 2,
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                border: '1px solid #2D2D2D',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PointOfSaleIcon sx={{ fontSize: 20, color: '#FFD700' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ 
                  color: '#FFD700',
                  fontWeight: 900,
                  fontSize: '1.5rem'
                }}>
                  {walletInfo.wallet_balance?.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                </Typography>
              </Box>

              {/* Points Balance */}
              <Box sx={{ 
                p: 2,
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                border: '1px solid #2D2D2D',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StarsIcon sx={{ fontSize: 20, color: '#FFD700' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'نقاط مكتسبة' : 'Earning points'}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ 
                  color: '#FFFFFF',
                  fontWeight: 800,
                  fontSize: '1.5rem'
                }}>
                  {walletInfo.points_balance} {language === 'ar' ? 'نقطة' : 'point'}
                </Typography>
              </Box>

              {/* Points Value */}
              <Box sx={{ 
                p: 2,
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                border: '1px solid #2D2D2D',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StarsIcon sx={{ fontSize: 20, color: '#FFD700' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'قيمة النقاط' : 'Points value'}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ 
                  color: '#FFD700',
                  fontWeight: 900,
                  fontSize: '1.5rem'
                }}>
                  {walletInfo.points_value} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                </Typography>
              </Box>
            </Box>

            {/* Points Info */}
            <Box sx={{ 
              mt: 3,
              p: 2,
              backgroundColor: 'rgba(255, 215, 0, 0.05)',
              borderRadius: '12px',
              border: '1px solid #2D2D2D'
            }}>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? `💡 كل 1 نقطة = ${walletInfo?.points_value_int} ر.م · يمكن استخدام النقاط في المشتريات القادمة` : `💡 Every 1 point = ${walletInfo?.points_value_int} R.m · Points can be used on future purchases`}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Transactions History */}
      <Card sx={{ 
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '20px',
        boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          border: '1px solid #FFD700',
          boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <HistoryIcon sx={{ fontSize: 32, color: '#FFD700' }} />
            <Typography variant="h5" sx={{ 
              color: '#FFD700',
              fontWeight: 800
            }}>
              {language === 'ar' ? 'سجل المعاملات' : 'Transaction'}
            </Typography>
          </Box>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            mb: 3
          }} />

          <TableContainer component={Paper} sx={{ 
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: '12px',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid #2D2D2D',
              color: '#FFFFFF',
            }
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: '#1A1A1A',
                  '& th': {
                    fontWeight: 700,
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '2px solid #FFD700',
                  }
                }}>
                  <TableCell>{language === 'ar' ? 'التاريخ' : 'Date'}</TableCell>
                  <TableCell>{language === 'ar' ? 'القيمة' : 'Value'}</TableCell>
                  <TableCell>{language === 'ar' ? 'نقاط مكتسبة' : 'Earnig points'}</TableCell>
                  <TableCell>{language === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}</TableCell>
                  <TableCell>{language === 'ar' ? 'طريقة الدفع' : 'Payment method'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow 
                    key={tx.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.05)',
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ color: '#B3B3B3' }}>
                      {new Date(tx.created_at).toLocaleDateString('ar-EG')}
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFD700',
                      fontWeight: 600
                    }}>
                      {tx.amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`+${tx.points_earned} ${language === 'ar' ? 'نقطة' : 'Point'}`}
                        sx={{
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          color: '#FFD700',
                          border: '1px solid #FFD700',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#FFD700',
                            color: '#000000',
                          }
                        }}
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`+ ${(tx.points_earned * walletInfo?.points_value_int).toFixed(2)} ${language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}`}
                        sx={{
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          color: '#FFD700',
                          border: '1px solid #FFD700',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#FFD700',
                            color: '#000000',
                          }
                        }}
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#FFFFFF' }}>
                        {tx.payment_method === 'cash_on_delivery' ? cashOnDelvryTxt: 
                         tx.payment_method === 'wallet' ? walletTxt: 
                         tx.payment_method?.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <HistoryIcon sx={{ fontSize: 48, color: '#2D2D2D', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#B3B3B3' }}>
                          {language === 'ar' ? 'لا توجد معاملات حتى الآن' : 'No transactions yet.'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B6B6B', mt: 1 }}>
                          {language === 'ar' ? 'ستظهر معاملاتك هنا عند إجراء أي عملية شراء' : 'Your transactions will appear here when you make any purchase.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary Footer */}
          {transactions.length > 0 && (
            <Box sx={{ 
              mt: 3,
              p: 2,
              backgroundColor: '#1A1A1A',
              borderRadius: '12px',
              border: '1px solid #2D2D2D',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'إجمالي المعاملات: ' : 'Total transaction : '} {transactions.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                    {language === 'ar' ? 'إجمالي النقاط المكتسبة' : 'Total Earning points'}
                  </Typography>
                  <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>
                    +{transactions.reduce((sum, tx) => sum + (tx.points_earned || 0), 0)} {language === 'ar' ? 'نقطة' : 'Point'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                    {language === 'ar' ? 'إجمالي قيمة النقاط' : 'Total points value'}
                  </Typography>
                  <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>
                    {(transactions.reduce((sum, tx) => sum + (tx.points_earned || 0), 0) * walletInfo?.points_value_int).toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default WalletPage;